import { supabase } from '../lib/supabase';

interface ImpersonationData {
  originalUserId: string;
  originalBusinessId: string;
  targetUserId: string;
  targetBusinessId: string;
  targetUserEmail: string;
  timestamp: string;
}

export class ImpersonationService {
  private static readonly STORAGE_KEY = 'nexus_impersonation_data';

  /**
   * Start impersonating a user
   */
  static async startImpersonation(targetUserData: any): Promise<{ success: boolean; message: string }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }

    try {
      // Get current user session to store as "original"
      const { data: currentSession } = await supabase.auth.getSession();
      
      if (!currentSession?.session?.user) {
        return { success: false, message: 'No current session found' };
      }

      // Get current user data
      const { data: currentUser } = await supabase
        .from('users')
        .select('id, business_id, email')
        .eq('id', currentSession.session.user.id)
        .single();

      if (!currentUser) {
        return { success: false, message: 'Current user data not found' };
      }

      // Store impersonation data
      const impersonationData: ImpersonationData = {
        originalUserId: currentUser.id,
        originalBusinessId: currentUser.business_id,
        targetUserId: targetUserData.id,
        targetBusinessId: targetUserData.business_id,
        targetUserEmail: targetUserData.email,
        timestamp: new Date().toISOString()
      };

      // Store in localStorage
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(impersonationData));

      // Update the session to use the target user's data
      // Note: We'll simulate the user switch by updating the business context
      // The actual implementation depends on your auth system

      console.log('👤 Impersonation started:', {
        from: currentUser.email,
        to: targetUserData.email,
        business: targetUserData.business_name
      });

      return { success: true, message: `Now impersonating ${targetUserData.email}` };

    } catch (error) {
      console.error('❌ Error starting impersonation:', error);
      return { success: false, message: 'Failed to start impersonation' };
    }
  }

  /**
   * Stop impersonating and return to original user
   */
  static async stopImpersonation(): Promise<{ success: boolean; message: string }> {
    try {
      const impersonationData = this.getImpersonationData();
      
      if (!impersonationData) {
        return { success: false, message: 'No active impersonation session' };
      }

      // Clear the impersonation data
      localStorage.removeItem(this.STORAGE_KEY);

      console.log('👤 Impersonation stopped, returning to original user');

      return { success: true, message: 'Returned to original user' };

    } catch (error) {
      console.error('❌ Error stopping impersonation:', error);
      return { success: false, message: 'Failed to stop impersonation' };
    }
  }

  /**
   * Check if currently impersonating
   */
  static isImpersonating(): boolean {
    return !!this.getImpersonationData();
  }

  /**
   * Get current impersonation data
   */
  static getImpersonationData(): ImpersonationData | null {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  /**
   * Get impersonation status info
   */
  static getImpersonationStatus(): {
    isImpersonating: boolean;
    targetUser?: string;
    originalUser?: string;
    duration?: string;
  } {
    const data = this.getImpersonationData();
    
    if (!data) {
      return { isImpersonating: false };
    }

    const startTime = new Date(data.timestamp);
    const now = new Date();
    const duration = Math.floor((now.getTime() - startTime.getTime()) / 1000 / 60); // minutes

    return {
      isImpersonating: true,
      targetUser: data.targetUserEmail,
      originalUser: 'Super Admin',
      duration: `${duration} minutes`
    };
  }

  /**
   * Force a page reload to apply impersonation changes
   */
  static reloadToApplyImpersonation(): void {
    // Reload the page to trigger context updates
    window.location.href = '/';
  }
} 